export type ProcessingNoteCode =
    | 'TD'
    | 'IN'
    | 'VM'
    | 'VD'
    | 'TM'
    | 'TO'
    | 'DM'
    | 'SUPPLIER_MISSING'
    | 'CONTACT_MISSING'
    | 'ISSUER_IS_BUSINESS'
    | 'PERIOD_LOCK'
    | 'MISSING_TRANSACTIONS'
    | 'UNRECOGNIZED_TYPE'
    | 'YEAR_UNKNOWN'
    | 'DUPLICATE'
    | 'COUNTRY_UNSUPPORTED'
    | 'PASSWORD_PROTECTED'
    | 'LOW_QUALITY'
    | 'CURRENCY_MISSING'
    | 'MULTIPAGE'
    | 'TAX_MISMATCH'
    | 'INVALID_DATE'
    | 'BANK_MISSING'
    | 'CURRENCY_MISMATCH';

export interface ProcessingNote {
    code: ProcessingNoteCode;
    label: string;
    value: string;
}

export const PROCESSING_NOTES: Record<ProcessingNoteCode, ProcessingNote> = {
    TD: {
        code: 'TD',
        label: 'TD - Document Date Missing',
        value: 'TD - Document Date Missing: Document or Due/Payment Date not available instead the processing date has been used.',
    },
    IN: {
        code: 'IN',
        label: 'IN - Document Reference Missing',
        value: 'IN - Document Reference Missing: Document reference not found; therefore a Debitle generated number has been used.',
    },
    VM: {
        code: 'VM',
        label: 'VM - VAT/GST Rate Missing',
        value: 'VM - VAT/GST Rate Missing: VAT/GST Rate not found in the document; please recheck and update if needed.',
    },
    VD: {
        code: 'VD',
        label: 'VD - VAT/GST Rate Error',
        value: 'VD - VAT/GST Rate Error: The VAT/GST Rates are different for each line however for processing standard VAT rate has been selected.',
    },
    TM: {
        code: 'TM',
        label: 'TM - Total Amount Missing',
        value: 'TM - Total Amount Missing: No total amount was detected, therefore set to zero.',
    },
    TO: {
        code: 'TO',
        label: 'T0 - Transaction Amount Zero',
        value: 'T0 - Transaction Amount Zero: Transaction amounts identified as zero, please re-check.',
    },
    DM: {
        code: 'DM',
        label: 'DM - Description Missing',
        value: 'DM - Description Missing: Product description not found; therefore contact name used.',
    },
    SUPPLIER_MISSING: {
        code: 'SUPPLIER_MISSING',
        label: 'Supplier Name Missing',
        value: 'Supplier Name Missing: Supplier or vendor details could not be identified from the document.',
    },
    CONTACT_MISSING: {
        code: 'CONTACT_MISSING',
        label: 'Contact Missing',
        value: 'Contact Missing: Contact name is not found in the document; please update manually.',
    },
    ISSUER_IS_BUSINESS: {
        code: 'ISSUER_IS_BUSINESS',
        label: 'Issuer matches business name',
        value: 'The issuer name matches the business name: Please check and reclassify to sales if it is a sales document.',
    },
    PERIOD_LOCK: {
        code: 'PERIOD_LOCK',
        label: 'PL - Before period lock date',
        value: 'PL - The document is before the period lock date: Please update the period lock setting to record this transaction.',
    },
    MISSING_TRANSACTIONS: {
        code: 'MISSING_TRANSACTIONS',
        label: 'Missing transactions',
        value: 'Missing transactions: The uploaded Invoice appears incomplete as the total gross amount does not match the calculated line amount.',
    },
    UNRECOGNIZED_TYPE: {
        code: 'UNRECOGNIZED_TYPE',
        label: 'Unrecognized Type',
        value: 'Unrecognized Type: The document not recognized as an invoice or receipt.',
    },
    YEAR_UNKNOWN: {
        code: 'YEAR_UNKNOWN',
        label: 'Invoice year not identified',
        value: 'Invoice year is not identified: Therefore current year selected in the invoice date.',
    },
    DUPLICATE: {
        code: 'DUPLICATE',
        label: 'Duplicate document',
        value: 'Duplicate document: Debitle has identified this document as duplicate of another processed document; please review for further processing.',
    },
    COUNTRY_UNSUPPORTED: {
        code: 'COUNTRY_UNSUPPORTED',
        label: 'Country not supported',
        value: 'Country not supported: Debitle does not support the documents for this country at the moment.',
    },
    PASSWORD_PROTECTED: {
        code: 'PASSWORD_PROTECTED',
        label: 'Password Protected Document',
        value: 'Password Protected Document: The document is password protected and could not be processed.',
    },
    LOW_QUALITY: {
        code: 'LOW_QUALITY',
        label: 'Low Quality Image',
        value: 'Low Quality Image: Some or all of the required data could not be extracted.',
    },
    CURRENCY_MISSING: {
        code: 'CURRENCY_MISSING',
        label: 'Currency Code Missing',
        value: 'Currency Code Missing: Currency symbol/code not found; default currency was assigned.',
    },
    MULTIPAGE: {
        code: 'MULTIPAGE',
        label: 'Multi-page Document Issue',
        value: 'Multi-page Document Issue: Document contains multiple pages, only the first page was processed.',
    },
    TAX_MISMATCH: {
        code: 'TAX_MISMATCH',
        label: 'Tax Amount Mismatch',
        value: 'Tax Amount Mismatch: Subtotal and tax rate do not sum up to the total amount.',
    },
    INVALID_DATE: {
        code: 'INVALID_DATE',
        label: 'Invalid Date Format',
        value: 'Invalid Date Format: Document date is in an unrecognized or ambiguous format.',
    },
    BANK_MISSING: {
        code: 'BANK_MISSING',
        label: 'Bank Details Missing',
        value: 'Bank Details Missing: Payment instructions or bank account numbers were not found.',
    },
    CURRENCY_MISMATCH: {
        code: 'CURRENCY_MISMATCH',
        label: 'Transaction currency mismatch',
        value: 'Transaction currency does not match the account currency.',
    },
};

export const PROCESSING_NOTE_OPTIONS = Object.values(PROCESSING_NOTES).map((n) => ({
    label: n.label,
    value: n.value,
    code: n.code,
}));

export function appendProcessingNote(
    current: Array<{ label: string; value: string }> | undefined | null,
    code: ProcessingNoteCode,
): Array<{ label: string; value: string }> {
    const note = PROCESSING_NOTES[code];
    const list = Array.isArray(current) ? current : [];
    if (list.some((n) => n.value === note.value)) return list;
    return [...list, { label: note.label, value: note.value }];
}

export function formatElapsed(iso: string | number | Date | null | undefined): string {
    if (!iso) return '';
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return '';
    const now = Date.now();
    const diff = Math.max(0, now - then);
    const sec = Math.floor(diff / 1000);
    if (sec < 60) return `${sec} second${sec === 1 ? '' : 's'} ago`;
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min} minute${min === 1 ? '' : 's'} ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr} hour${hr === 1 ? '' : 's'} ago`;
    const day = Math.floor(hr / 24);
    if (day < 7) return `${day} day${day === 1 ? '' : 's'} ago`;
    const wk = Math.floor(day / 7);
    if (wk < 5) return `${wk} week${wk === 1 ? '' : 's'} ago`;
    const mo = Math.floor(day / 30);
    if (mo < 12) return `${mo} month${mo === 1 ? '' : 's'} ago`;
    const yr = Math.floor(day / 365);
    return `${yr} year${yr === 1 ? '' : 's'} ago`;
}

export function formatDurationMs(ms: number | null | undefined): string {
    if (!ms || ms <= 0) return 'Active';
    const sec = Math.floor(ms / 1000);
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m`;
}
